import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import * as apiLib from "@/lib/api";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setCurrent(""); setNext(""); setConfirm(""); setError(""); }
  }, [open]);

  const submit = async () => {
    setError("");
    if (next.length < 6) { setError("New password must be at least 6 characters"); return; }
    if (next !== confirm) { setError("New passwords do not match"); return; }
    setLoading(true);
    try {
      await apiLib.changePassword(current, next);
      toast.success("Password updated");
      onOpenChange(false);
    } catch (e) {
      setError(apiLib.formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight">Change Password</DialogTitle>
          <DialogDescription>Update the password for your account.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} data-testid="current-password"
              autoComplete="current-password" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" value={next}
              onChange={(e) => setNext(e.target.value)} data-testid="new-password"
              placeholder="min 6 characters" autoComplete="new-password" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input id="cp-confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password"
              autoComplete="new-password" />
          </div>
          {error && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="change-password-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="change-password-cancel">Cancel</Button>
          <Button onClick={submit} disabled={loading || !current || !next || !confirm} data-testid="change-password-submit">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
            Update Password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
